import React, { useCallback } from 'react'
import { Form } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useClinicOperatories } from '@hooks/useClinicOperatories'
import { setActiveOperatory } from '@store/reducers/operatorySession.reducer'

/**
 * Per-session "which chair am I at?" picker for active-patient DICOM routing.
 *
 * The selection is persisted per-workstation (localStorage) so a clinician sets
 * it once. While a patient chart / X-ray viewer is open, the selected chair is
 * paired with the open patient (see `usePublishActivePatient`) so images
 * captured at that operatory route to the right patient.
 */
const OperatoryChairPicker: React.FC<{ className?: string }> = ({
  className,
}) => {
  const dispatch = useAppDispatch()
  useClinicOperatories()
  const operatories = useAppSelector((s) => s.operatory.data)
  const selectedOperatoryId = useAppSelector(
    (s) => s.operatorySession.selectedOperatoryId,
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      dispatch(setActiveOperatory(e.target.value || null))
    },
    [dispatch],
  )

  return (
    <Form.Select
      id="operatory-chair-picker"
      className={className}
      size="sm"
      value={selectedOperatoryId ?? ''}
      onChange={handleChange}
      aria-label="My chair"
      title="Route images captured at this chair to the patient you have open"
    >
      <option value="">My chair</option>
      {operatories.map((op) => (
        <option key={op.operatory_id} value={op.operatory_id}>
          {op.name}
        </option>
      ))}
    </Form.Select>
  )
}

export default OperatoryChairPicker
